import { produce } from 'immer';
import * as actions from 'store/actions';
import {
  Organisation,
  OrganisationListItem,
  OrganisationsState,
} from 'store/organisations/types';
import { createReducer } from 'typesafe-actions';

const EMPTY_ORGANISATION_LIST_ITEM: OrganisationListItem = {
  id: '',
  reference: '',
  dv: {},
  dvl: {},
  details: {
    name: '',
  },
  assessment_statuses: {},
  assessmentTemplateIds: [],
  compliance_subtypes: {},
};

const mergeOrganisationIntoListItem = (
  existing: OrganisationListItem | undefined,
  next: Partial<Organisation>,
): OrganisationListItem => ({
  ...EMPTY_ORGANISATION_LIST_ITEM,
  ...existing,
  // Data coming straight from sync payload
  ...Object.fromEntries(
    Object.entries({
      assessment_statuses: next.assessment_statuses,
      compliance_subtypes: next.compliance_subtypes,
      details: next.details,
      id: next.id,
      reference: next.reference,
    }).filter(([, value]) => value !== undefined),
  ),
  // Data calculated from sync payload
  ...Object.fromEntries(
    Object.entries({
      assessmentTemplateIds: next.assessmentTemplates
        ? Object.keys(next.assessmentTemplates)
        : undefined,
    }).filter(([, value]) => value !== undefined),
  ),
  dvl: {
    ...existing?.dvl,
    ...next.dvl,
  },
  dv: {
    ...existing?.dv,
    ...next.dv,
  },
});

const initialState: OrganisationsState = {
  list: {},
  loaded: {},
  loadedCount: {},
  localMerge: {},
};

export const organisationReducer = createReducer(initialState)
  .handleAction(actions.LOAD_ORGANISATION.success, (state, action) => ({
    ...state,
    loadedCount: {
      ...state.loadedCount,
      [action.payload]: (state.loadedCount[action.payload] || 0) + 1,
    },
  }))
  .handleAction(actions.SET_LOADED_ORGANISATION, (state, action) => ({
    ...state,
    loaded: { ...state.loaded, [action.payload.id]: action.payload },
  }))
  .handleAction(actions.UNSET_LOADED_ORGANISATION, (state, action) =>
    produce(state, (draft) => {
      const newCount = Math.max(
        (state.loadedCount[action.payload] || 1) - 1,
        0,
      );
      if (!newCount) {
        delete draft.loaded[action.payload];
        delete draft.loadedCount[action.payload];
      } else {
        draft.loadedCount[action.payload] = newCount;
      }
    }),
  )
  .handleAction(actions.UPDATE_ORGANISATION_LIST, (state, action) =>
    produce(state, (draft) => {
      for (const id of Object.keys(action.payload.organisations)) {
        draft.list[id] = mergeOrganisationIntoListItem(
          state.list[id],
          action.payload.organisations[id]!,
        );
      }
    }),
  )
  .handleAction(actions.DELETE_ORGANISATIONS, (state, action) =>
    produce(state, (draft) => {
      action.payload.ids.forEach((organisationId) => {
        delete draft.list[organisationId];
        delete draft.loaded[organisationId];
        delete draft.loadedCount[organisationId];
      });
    }),
  )
  .handleAction(actions.doOrgLocalMergeData, (state, action) =>
    produce(state, (draft) => {
      if (!draft.localMerge[action.payload.id]) {
        draft.localMerge[action.payload.id] = {};
      }
      const localDraft = draft.localMerge[action.payload.id]!;
      if (action.payload.data.actionSuggestions) {
        Object.entries(action.payload.data.actionSuggestions).forEach(
          ([id, item]) => {
            if (!localDraft.actionSuggestions) {
              localDraft.actionSuggestions = {};
            }
            localDraft.actionSuggestions[id] = item;
          },
        );
      }
      if (action.payload.data.controlSuggestions) {
        Object.entries(action.payload.data.controlSuggestions).forEach(
          ([id, item]) => {
            if (!localDraft.controlSuggestions) {
              localDraft.controlSuggestions = {};
            }
            localDraft.controlSuggestions[id] = item;
          },
        );
      }
    }),
  )
  .handleAction(actions.doOrgLocalMergeRemoveKeys, (state, action) =>
    produce(state, (draft) => {
      const currentData = draft.localMerge[action.payload.id];
      if (currentData) {
        (action.payload.keys as (keyof typeof currentData)[]).forEach((key) => {
          delete currentData[key];
        });
        if (!Object.keys(currentData).length) {
          delete draft.localMerge[action.payload.id];
        } else {
          draft.localMerge[action.payload.id] = currentData;
        }
      }
      return draft;
    }),
  )
  .handleAction(actions.RESET_DEV_DATA, (state) => ({
    ...state,
    loaded: {},
    loadedCount: {},
  }))
  .handleAction(actions.SIGN_OUT.success, () => initialState);
